import { useState, useCallback, useEffect, useRef } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Scheduler request game from API
 */
export interface SchedulerRequestGame {
  id: string
  scheduler_request_id: string
  schedule_id: string
  division_uid: string
  home_team: { id: string; name: string } | null
  visiting_team: { id: string; name: string } | null
  venue: { id: string; name: string; timezone: string | null } | null
  facility: { id: string; name: string } | null
  starts_at: string
  ends_at: string
  game_number: number
  approval_status: 'pending' | 'approved' | 'rejected'
  rejection_reason: string | null
  reviewed_by: { id: string; name: string } | null
  reviewed_at: string | null
  can_review: boolean
  is_applied: boolean
  game_id: string | null
  created_at: string
  updated_at: string
}

/**
 * Scheduler constraint violation from API
 */
export interface SchedulerViolation {
  id: string
  constraint_type: {
    id: string
    code: string
    name: string
    category: string
  } | null
  severity: 'error' | 'warning' | 'info'
  message: string
  resolution_suggestion: string | null
  context: Record<string, unknown> | null
  affected_game_numbers: number[] | null
  affected_team_uids: string[] | null
  is_prevented: boolean
  original_slot: Record<string, unknown> | null
  resolved_slot: Record<string, unknown> | null
}

/**
 * Violation detail from the solver diagnostic output.
 * Extracted from the job's response_payload by the API.
 */
export interface DivisionJobViolation {
  message: string
  resolution_suggestion: string | null
  constraint_type: string | null
  constraint_name: string | null
  severity: string
}

/**
 * Per-division job status from the scheduler API.
 * Each division being scheduled gets its own job entry.
 */
export interface DivisionJob {
  id: string
  division_id: string
  division_name: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  satisfaction_score: number | null
  games_count: number
  error_code: string | null
  error_message: string | null
  violations: DivisionJobViolation[]
  started_at: string | null
  completed_at: string | null
}

/**
 * Capabilities that the API reports for the current request state.
 * Drives which action buttons the UI renders.
 */
export interface SchedulerRequestCapabilities {
  can_retry: boolean
  can_reset: boolean
  can_cancel: boolean
  can_approve: boolean
  can_apply: boolean
  can_publish: boolean
  can_checkpoint: boolean
}

/**
 * Scheduler request structure from API
 */
export interface SchedulerRequest {
  id: string
  request_id: string | null
  status:
    | 'pending'
    | 'processing'
    | 'draft'
    | 'partially_drafted'
    | 'failed'
    | 'applied'
    | 'cancelled'
  schedule_id: string | null
  created_by: {
    id: string
    name: string
  } | null
  total_games_count: number
  approved_games_count: number
  rejected_games_count: number
  pending_games_count: number | null
  satisfaction_score: number | null
  progress: number | null
  error: string | { code: string; message: string; details: unknown } | null
  approved_division_uids?: string[]
  locked_division_uids?: string[] // backward compat
  division_dispatch_order?: string[]
  has_checkpoint?: boolean
  checkpoint_saved_at?: string | null
  checkpoint_game_count?: number | null
  checkpoint_saved_by?: 'auto' | 'user' | null
  has_changes_since_checkpoint?: boolean
  version_number: number | null
  label: string | null
  can_retry: boolean
  can_apply: boolean
  can_cancel: boolean
  division_jobs?: DivisionJob[]
  capabilities?: SchedulerRequestCapabilities
  violations_count?: number
  violations?: SchedulerViolation[]
  games?: SchedulerRequestGame[]
  submitted_at: string | null
  generated_at: string | null
  applied_at: string | null
  created_at: string
  updated_at: string
}

/** Distinguishes the two timeout reasons for the UI */
export type TimeoutReason = 'total' | 'stuck'

const POLL_INTERVAL_MS = 3000

/**
 * Compute the hard total-ceiling timeout based on the number of divisions.
 * Formula from arch doc: 5 min base + 30s per division
 */
function computeTotalTimeoutMs(divisionCount: number): number {
  return 5 * 60_000 + divisionCount * 30_000
}

/** Stuck timer: if progress doesn't change for 4 minutes */
const STUCK_TIMEOUT_MS = 4 * 60_000

/**
 * Derive a progress fingerprint from division_jobs so we can detect
 * when *any* status changes (pending->processing, processing->completed, etc.).
 */
function deriveProgressFingerprint(
  divisionJobs: DivisionJob[] | undefined
): string {
  if (!divisionJobs || divisionJobs.length === 0) {
    return ''
  }
  return divisionJobs.map((j) => `${j.id}:${j.status}`).join('|')
}

/**
 * Hook result interface
 */
interface UseSchedulerRequestResult {
  request: SchedulerRequest | null
  requestId: string | null
  loading: boolean
  error: Error | null
  isResuming: boolean
  isPolling: boolean
  isTimedOut: boolean
  timeoutReason: TimeoutReason | null
  isStuck: boolean
  createRequest: (
    scheduleId: string,
    flightId?: string | null
  ) => Promise<string>
  fetchRequest: (requestId: string) => Promise<void>
  resetDivisions: (
    requestId: string,
    divisionUids: string[]
  ) => Promise<boolean>
  cancelRequest: (requestId: string) => Promise<boolean>
  retryRequest: (requestId: string) => Promise<boolean>
  retryFailedDivisions: (requestId: string) => Promise<boolean>
  clearRequest: () => void
  startPolling: (requestUid: string, divisionCount?: number) => void
  stopPolling: () => void
  keepWaiting: () => void
  updateDivisionApprovalStatus: (
    divisionUids: string[],
    approvalStatus: SchedulerRequestGame['approval_status']
  ) => void
  updateAllGamesApproved: () => void
}

/**
 * Custom hook for managing scheduler request state.
 * Supports lazy creation, resuming existing requests, polling
 * for schedule generation status, two-timer timeout (total ceiling + stuck),
 * cancel, retry, and per-division progress tracking.
 */
export const useSchedulerRequest = (
  initialRequestId?: string
): UseSchedulerRequestResult => {
  const [request, setRequest] = useState<SchedulerRequest | null>(null)
  const [requestId, setRequestId] = useState<string | null>(
    initialRequestId || null
  )

  // Sync requestId state when URL param changes (e.g., flight switch navigation)
  useEffect(() => {
    if (initialRequestId !== undefined) {
      setRequestId(initialRequestId || null)
    }
    if (initialRequestId === undefined) {
      // Navigated to /configure with no requestId (new flight flow)
      setRequestId(null)
      setRequest(null)
      setIsResuming(false)
    }
  }, [initialRequestId])

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [isResuming, setIsResuming] = useState<boolean>(!!initialRequestId)
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [isTimedOut, setIsTimedOut] = useState<boolean>(false)
  const [timeoutReason, setTimeoutReason] = useState<TimeoutReason | null>(null)
  const [isStuck, setIsStuck] = useState<boolean>(false)

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartTimeRef = useRef<number | null>(null)
  const lastProgressFingerprintRef = useRef<string>('')
  const lastProgressTimeRef = useRef<number>(Date.now())
  const totalTimeoutMsRef = useRef<number>(computeTotalTimeoutMs(1))

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  /**
   * Fetch an existing scheduler request by ID
   */
  const fetchRequest = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      const { data } = await req(`/scheduler/requests/${id}`)
      const requestData = data?.data ?? data
      setRequest(requestData)
      setRequestId(id)
      return requestData
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching the scheduler request')
      )
      setRequest(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Poll for request status updates with two-timer timeout logic.
   */
  const pollRequest = useCallback(
    async (uid: string) => {
      try {
        const { data } = await req(`/scheduler/requests/${uid}`)
        const requestData = data?.data ?? data
        setRequest(requestData)

        const status = requestData?.status

        // Terminal statuses: stop polling
        if (
          status === 'draft' ||
          status === 'partially_drafted' ||
          status === 'failed' ||
          status === 'cancelled' ||
          status === 'applied'
        ) {
          clearPollInterval()
          setIsPolling(false)
          return
        }

        // --- Two-timer timeout logic ---
        const now = Date.now()

        // 1) Check total ceiling timer
        if (
          pollStartTimeRef.current &&
          now - pollStartTimeRef.current > totalTimeoutMsRef.current
        ) {
          clearPollInterval()
          setIsPolling(false)
          setIsTimedOut(true)
          setTimeoutReason('total')
          return
        }

        // 2) Track progress changes via division_jobs fingerprint
        const fingerprint = deriveProgressFingerprint(
          requestData?.division_jobs
        )
        if (fingerprint !== lastProgressFingerprintRef.current) {
          // Progress changed — reset stuck timer
          lastProgressFingerprintRef.current = fingerprint
          lastProgressTimeRef.current = now
          setIsStuck(false)
        } else if (now - lastProgressTimeRef.current > STUCK_TIMEOUT_MS) {
          // No progress for 4 minutes — show stuck UI
          clearPollInterval()
          setIsPolling(false)
          setIsStuck(true)
          setIsTimedOut(true)
          setTimeoutReason('stuck')
          return
        }
      } catch {
        // On network error, keep polling (don't stop)
      }
    },
    [clearPollInterval]
  )

  /**
   * Start polling for request status.
   * @param requestUid - the scheduler request ID
   * @param divisionCount - number of divisions being scheduled (for dynamic timeout)
   */
  const startPolling = useCallback(
    (requestUid: string, divisionCount?: number) => {
      clearPollInterval()
      setIsPolling(true)
      setIsTimedOut(false)
      setTimeoutReason(null)
      setIsStuck(false)
      setRequestId(requestUid)
      pollStartTimeRef.current = Date.now()
      lastProgressTimeRef.current = Date.now()
      lastProgressFingerprintRef.current = ''
      totalTimeoutMsRef.current = computeTotalTimeoutMs(divisionCount ?? 1)

      // Initial fetch immediately
      pollRequest(requestUid)

      // Then poll at interval
      pollIntervalRef.current = setInterval(() => {
        pollRequest(requestUid)
      }, POLL_INTERVAL_MS)
    },
    [clearPollInterval, pollRequest]
  )

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    clearPollInterval()
    setIsPolling(false)
    pollStartTimeRef.current = null
  }, [clearPollInterval])

  /**
   * Dismiss timeout / stuck state and resume polling.
   * Called when the user clicks "Keep Waiting".
   */
  const keepWaiting = useCallback(() => {
    if (!requestId) {
      return
    }
    setIsTimedOut(false)
    setTimeoutReason(null)
    setIsStuck(false)
    // Reset the stuck timer baseline
    lastProgressTimeRef.current = Date.now()
    // Resume polling (keep the total timer running from original start)
    setIsPolling(true)
    pollRequest(requestId)
    pollIntervalRef.current = setInterval(() => {
      pollRequest(requestId)
    }, POLL_INTERVAL_MS)
  }, [requestId, pollRequest])

  /**
   * Create a new scheduler request for the given schedule
   */
  const createRequest = useCallback(
    async (scheduleId: string, flightId?: string | null): Promise<string> => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await req('/scheduler/requests', {
          method: 'POST',
          body: JSON.stringify({
            schedule_id: scheduleId,
            ...(flightId ? { scheduler_flight_id: flightId } : {}),
          }),
        })

        const requestData = data?.data ?? data
        setRequest(requestData)
        setRequestId(requestData.id)
        setIsResuming(false)

        return requestData.id
      } catch (err: unknown) {
        const caughtError =
          err instanceof Error
            ? err
            : new Error(
                'An error occurred while creating the scheduler request'
              )
        setError(caughtError)
        throw caughtError
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Reset specific divisions back to pending by removing their generated games
   */
  const resetDivisions = useCallback(
    async (id: string, divisionUids: string[]): Promise<boolean> => {
      try {
        const { data } = await req(
          `/scheduler/requests/${id}/reset-divisions`,
          {
            method: 'POST',
            body: JSON.stringify({ division_uids: divisionUids }),
          }
        )
        const requestData = data?.data ?? data
        setRequest(requestData)
        return true
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Operation failed'))
        return false
      }
    },
    []
  )

  /**
   * Cancel a scheduler request that is currently processing
   */
  const cancelRequest = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { data } = await req(`/scheduler/requests/${id}/cancel`, {
          method: 'POST',
        })
        const requestData = data?.data ?? data
        setRequest(requestData)
        clearPollInterval()
        setIsPolling(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Operation failed'))
        return false
      }
    },
    [clearPollInterval]
  )

  /**
   * Retry a failed/cancelled scheduler request (all divisions)
   */
  const retryRequest = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { data } = await req(`/scheduler/requests/${id}/retry`, {
        method: 'POST',
      })
      const requestData = data?.data ?? data
      setRequest(requestData)
      return true
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Operation failed'))
      return false
    }
  }, [])

  /**
   * Retry only the failed divisions of a partially_generated request
   */
  const retryFailedDivisions = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { data } = await req(`/scheduler/requests/${id}/retry`, {
          method: 'POST',
          body: JSON.stringify({ failed_only: true }),
        })
        const requestData = data?.data ?? data
        setRequest(requestData)
        return true
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Operation failed'))
        return false
      }
    },
    []
  )

  /**
   * Update all games in specific divisions + recompute approved_division_uids (no refetch)
   */
  const updateDivisionApprovalStatus = useCallback(
    (
      divisionUids: string[],
      approvalStatus: SchedulerRequestGame['approval_status']
    ) => {
      setRequest((prev) => {
        if (!prev?.games) {
          return prev
        }
        const divSet = new Set(divisionUids)
        const updatedGames = prev.games.map((g) =>
          divSet.has(g.division_uid)
            ? { ...g, approval_status: approvalStatus }
            : g
        )
        const approvedDivUids = [
          ...new Set(updatedGames.map((g) => g.division_uid)),
        ].filter((uid) =>
          updatedGames
            .filter((g) => g.division_uid === uid)
            .every((g) => g.approval_status === 'approved')
        )
        return {
          ...prev,
          games: updatedGames,
          approved_division_uids: approvedDivUids,
        }
      })
    },
    []
  )

  /**
   * Mark ALL games as approved + update approved_division_uids (no refetch)
   */
  const updateAllGamesApproved = useCallback(() => {
    setRequest((prev) => {
      if (!prev?.games) {
        return prev
      }
      const updatedGames = prev.games.map((g) => ({
        ...g,
        approval_status: 'approved' as const,
      }))
      const allDivUids = [...new Set(updatedGames.map((g) => g.division_uid))]
      return {
        ...prev,
        games: updatedGames,
        approved_division_uids: allDivUids,
      }
    })
  }, [])

  /**
   * Clear the current request state
   */
  const clearRequest = useCallback(() => {
    stopPolling()
    setRequest(null)
    setRequestId(null)
    setError(null)
    setIsResuming(false)
    setIsTimedOut(false)
    setTimeoutReason(null)
    setIsStuck(false)
  }, [stopPolling])

  // Auto-fetch request when initialRequestId changes
  useEffect(() => {
    if (initialRequestId) {
      setIsResuming(true)
      fetchRequest(initialRequestId)
    }
  }, [initialRequestId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPollInterval()
    }
  }, [clearPollInterval])

  return {
    request,
    requestId,
    loading,
    error,
    isResuming,
    isPolling,
    isTimedOut,
    timeoutReason,
    isStuck,
    createRequest,
    fetchRequest,
    resetDivisions,
    cancelRequest,
    retryRequest,
    retryFailedDivisions,
    clearRequest,
    startPolling,
    stopPolling,
    keepWaiting,
    updateDivisionApprovalStatus,
    updateAllGamesApproved,
  }
}

export default useSchedulerRequest
